import { AiValidationFieldResultDto, AiValidationResponseDataDto, FormFieldDto } from '../../general/dtos';
import type { UserAnswerVerificationResult } from './rendering-helpers';
import { hasExpectedAnswer, isMultiChoiceType, selectionScore } from './multi-choice-fields';
import { fieldWeight } from '../../general/field-weight';

function isFreeTextType(type: FormFieldDto['type']): boolean {
    return type === 'text' || type === 'textarea' || type === 'inline-text';
}

export function isAiQueryableResult(result: UserAnswerVerificationResult): boolean {
    return hasExpectedAnswer(result.expectedAnswer) || Boolean(result.field.aiHint?.trim());
}

export function needsAutomaticAiVerification(results: UserAnswerVerificationResult[]): boolean {
    return results.some(result => isFreeTextType(result.field.type)
        && isAiQueryableResult(result)
        && (!hasExpectedAnswer(result.expectedAnswer) || result.isCorrect === false));
}

export function includeFreeTextResults(results: UserAnswerVerificationResult[], fields: FormFieldDto[], values: Record<string, any>): UserAnswerVerificationResult[] {
    return [...results, ...fields.filter(field => isFreeTextType(field.type)
        && (hasExpectedAnswer(field.expectedValue) || Boolean(field.aiHint?.trim()))
        && Object.prototype.hasOwnProperty.call(values, field.key) && !results.some(result => result.field.key === field.key))
        .map(field => ({ field, userAnswer: String(values[field.key] ?? ''), expectedAnswer: null, isCorrect: null }))];
}

export function aiValidationScore(result?: AiValidationFieldResultDto): number | null {
    if (result?.verdict === 'correct') return 1;
    if (result?.verdict === 'incorrect') return 0;
    if (result?.verdict !== 'partial') return null;
    return typeof result.score === 'number' && Number.isFinite(result.score) && result.score >= 0 && result.score <= 1
        ? result.score : 0.5;
}

export function verificationScore(result: UserAnswerVerificationResult, ai?: AiValidationFieldResultDto): number | null {
    const choiceScore = isMultiChoiceType(result.field.type) ? selectionScore(result.userAnswer, result.expectedAnswer) : null;
    return choiceScore ?? aiValidationScore(ai) ?? (hasExpectedAnswer(result.expectedAnswer, result.field.type) ? (result.isCorrect ? 1 : 0) : null);
}

export function normalizeAiChoiceResults(results: UserAnswerVerificationResult[], ai: AiValidationResponseDataDto): AiValidationResponseDataDto {
    return { ...ai, fieldResults: ai.fieldResults.map(aiField => {
        const result = results.find(result => result.field.key === aiField.fieldKey);
        const score = result && isMultiChoiceType(result.field.type) ? selectionScore(result.userAnswer, result.expectedAnswer) : null;
        return score === null ? aiField : { ...aiField, score, verdict: score === 1 ? 'correct' : score === 0 ? 'incorrect' : 'partial' };
    }) };
}

export function computeVerificationSummary(results: UserAnswerVerificationResult[], ai?: AiValidationResponseDataDto | null) {
    const fields = new Map((ai?.fieldResults ?? []).map(field => [field.fieldKey, field]));
    const checkable = (results ?? []).filter(result => hasExpectedAnswer(result.expectedAnswer, result.field.type)
        || (isFreeTextType(result.field.type) && isAiQueryableResult(result) && aiValidationScore(fields.get(result.field.key)) !== null));
    const earned = checkable.reduce((sum, result) => sum + (verificationScore(result, fields.get(result.field.key)) ?? 0) * fieldWeight(result.field), 0);
    const total = checkable.reduce((sum, result) => sum + fieldWeight(result.field), 0);
    return { total, correct: Number(earned.toFixed(6)), percentage: total ? Math.round(earned / total * 100) : 0 };
}
